import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Phone, Trash2, Inbox, Loader2, Calendar, MessageSquare, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ESTADOS = [
  { id: "novo", label: "Novo", color: "bg-yellow-400/15 text-yellow-300 border-yellow-400/40" },
  { id: "contactado", label: "Contactado", color: "bg-blue-400/15 text-blue-300 border-blue-400/40" },
  { id: "convertido", label: "Cliente", color: "bg-green-400/15 text-green-300 border-green-400/40" },
  { id: "arquivado", label: "Arquivado", color: "bg-neutral-400/10 text-neutral-400 border-neutral-700" },
];

const OBJ_LABELS = {
  ganho_muscular: "Ganho Muscular",
  perda_peso: "Perda de Peso",
  perda_gordura: "Perda de Gordura",
  tonificacao: "Tonificação",
  resistencia: "Resistência",
  mobilidade: "Mobilidade",
  pre_parto: "Pré-Parto",
  pos_parto: "Pós-Parto",
  jovens_atletas: "Jovens Atletas",
  senior_60: "Sénior +60",
  outro: "Outro",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function LeadsView() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos");

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data || []);
    } catch (e) {
      console.error("leads error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleEstado = async (id, estado) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { estado });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, estado } : l)));
    } catch (e) {
      console.error("update lead error", e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este contacto?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (e) {
      console.error("delete lead error", e);
    }
  };

  const filtrados =
    filtro === "todos" ? leads : leads.filter((l) => l.estado === filtro);

  const counts = ESTADOS.reduce((acc, e) => {
    acc[e.id] = leads.filter((l) => l.estado === e.id).length;
    return acc;
  }, {});

  return (
    <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16" data-testid="leads-view">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-10 bg-yellow-400" />
            <span className="overline">Pedidos de contacto · Redes sociais</span>
          </div>
          <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-tight">
            <Inbox className="inline mr-3 text-yellow-400" size={32} />
            Contactos recebidos
          </h2>
        </div>
        <button
          onClick={fetchLeads}
          className="btn-ghost text-xs inline-flex items-center gap-2"
          data-testid="leads-refresh"
        >
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFiltro("todos")}
          className={`chip ${filtro === "todos" ? "chip-active" : ""}`}
          data-testid="leads-filter-todos"
        >
          Todos · {leads.length}
        </button>
        {ESTADOS.map((e) => (
          <button
            key={e.id}
            onClick={() => setFiltro(e.id)}
            className={`chip ${filtro === e.id ? "chip-active" : ""}`}
            data-testid={`leads-filter-${e.id}`}
          >
            {e.label} · {counts[e.id] || 0}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card p-10 text-center text-neutral-400">
          <Loader2 className="animate-spin mx-auto mb-2" />
          A carregar contactos...
        </div>
      ) : filtrados.length === 0 ? (
        <div className="card p-10 text-center text-neutral-500" data-testid="leads-empty">
          Nenhum contacto {filtro !== "todos" ? `com estado "${filtro}"` : ""} de momento.
        </div>
      ) : (
        <div className="grid gap-4">
          {filtrados.map((l) => {
            const estadoCfg = ESTADOS.find((e) => e.id === l.estado) || ESTADOS[0];
            return (
              <div
                key={l.id}
                className="card p-5"
                data-testid={`lead-card-${l.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h3 className="font-display text-xl uppercase tracking-tight">
                        {l.nome}
                      </h3>
                      <span
                        className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border rounded ${estadoCfg.color}`}
                      >
                        {estadoCfg.label}
                      </span>
                      {l.objetivo ? (
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 border border-neutral-700 text-neutral-400 rounded">
                          {OBJ_LABELS[l.objetivo] || l.objetivo}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-neutral-300">
                      {l.email ? (
                        <a
                          href={`mailto:${l.email}`}
                          className="inline-flex items-center gap-2 hover:text-yellow-400"
                        >
                          <Mail size={14} /> {l.email}
                        </a>
                      ) : null}
                      {l.telefone ? (
                        <a
                          href={`tel:${l.telefone}`}
                          className="inline-flex items-center gap-2 hover:text-yellow-400"
                        >
                          <Phone size={14} /> {l.telefone}
                        </a>
                      ) : null}
                      <span className="inline-flex items-center gap-2 text-neutral-500">
                        <Calendar size={14} /> {fmtDate(l.created_at)}
                      </span>
                    </div>
                    {l.mensagem ? (
                      <div className="mt-3 text-sm text-neutral-400 bg-black/40 border border-neutral-800 rounded p-3 flex items-start gap-2">
                        <MessageSquare size={14} className="mt-0.5 shrink-0 text-yellow-400/70" />
                        <span className="whitespace-pre-wrap">{l.mensagem}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={l.estado}
                      onChange={(e) => handleEstado(l.id, e.target.value)}
                      className="input text-xs py-1.5"
                      data-testid={`lead-estado-${l.id}`}
                    >
                      {ESTADOS.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleDelete(l.id)}
                      className="text-neutral-500 hover:text-red-400 p-2"
                      data-testid={`lead-delete-${l.id}`}
                      aria-label="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
